import { getSession } from '../api';
import { logAccess, updateDocument } from './docuvaultService';

// ── Emergency Release Service ───────────────────────────────────────────────
// localStorage-based emergency release request tracking, consistent with the
// existing access log and expiry reminder patterns in docuvaultService.js.

const STORAGE_KEY = 'docuvault_emergency_requests';
const MAX_REQUESTS = 200;

export const EMERGENCY_TYPES = {
  death: {
    label: 'Death of Document Owner',
    icon: 'FiHeartCrack',
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    description: 'The document owner has passed away and these documents are needed for estate proceedings.',
  },
  medical: {
    label: 'Medical Emergency',
    icon: 'FiActivity',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    description: 'Urgent medical decisions require immediate access to healthcare directives or medical POA documents.',
  },
  legal: {
    label: 'Legal Proceedings',
    icon: 'FiScale',
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    description: 'Active legal proceedings require these documents as evidence or for court-mandated disclosure.',
  },
  incapacitation: {
    label: 'Owner Incapacitation',
    icon: 'FiAlertOctagon',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    description: 'The document owner is incapacitated and unable to grant access through normal channels.',
  },
  other: {
    label: 'Other Emergency',
    icon: 'FiAlertTriangle',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    description: 'Another type of emergency requiring expedited document access.',
  },
};

export const REQUEST_STATUS = {
  pending: { label: 'Pending Review', color: 'text-amber-600', bg: 'bg-amber-100', icon: 'FiClock' },
  approved: { label: 'Approved', color: 'text-green-600', bg: 'bg-green-100', icon: 'FiCheck' },
  denied: { label: 'Denied', color: 'text-red-600', bg: 'bg-red-100', icon: 'FiX' },
  released: { label: 'Released', color: 'text-green-700', bg: 'bg-green-200', icon: 'FiUnlock' },
  expired: { label: 'Expired', color: 'text-gray-500', bg: 'bg-gray-100', icon: 'FiClock' },
};

// ── Storage helpers ─────────────────────────────────────────────────────────

function getAllRequests() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveRequests(list) {
  try {
    if (list.length > MAX_REQUESTS) list = list.slice(0, MAX_REQUESTS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch { /* private mode */ }
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Creates a new emergency release request.
 * @param {object} doc - The document being requested
 * @param {object} requesterInfo - { name, email, role, relationship }
 * @param {string} emergencyType - key from EMERGENCY_TYPES
 * @param {string} description - justification text
 * @param {string} typedSignature - typed signature
 * @param {string} supportingEvidence - description of supporting evidence
 * @returns {object} The created request
 */
export function createEmergencyRequest(doc, requesterInfo, emergencyType, description, typedSignature, supportingEvidence = '') {
  const user = getSession();
  const request = {
    id: crypto.randomUUID(),
    docId: String(doc.id),
    docName: doc.original_name || '',
    docCategory: doc.file_category || 'general',
    s3Key: doc.s3_key || '',
    accessLevel: doc.access_level || 'private',
    emergencyType,
    description: description || '',
    supportingEvidence: supportingEvidence || '',
    requester: {
      name: requesterInfo.name || '',
      email: (requesterInfo.email || '').toLowerCase(),
      role: requesterInfo.role || 'other',
      relationship: requesterInfo.relationship || '',
      userId: user?.id || 'unknown',
    },
    typedSignature: typedSignature || '',
    status: 'pending',
    createdAt: new Date().toISOString(),
    reviewedAt: null,
    reviewedBy: null,
    reviewerNotes: '',
    releasedAt: null,
    expiresAt: null, // set on approval, 72h access window
    timeline: [
      {
        event: 'request_created',
        timestamp: new Date().toISOString(),
        actor: requesterInfo.name || 'Unknown',
        detail: `Emergency release request submitted (${EMERGENCY_TYPES[emergencyType]?.label || emergencyType})`,
      },
    ],
  };

  const list = getAllRequests();
  list.unshift(request);
  saveRequests(list);

  // Log in access log
  logAccess(doc.id, 'authorize', {
    docName: doc.original_name || '',
    emergencyType,
    requesterName: requesterInfo.name,
    action: 'emergency_request_created',
  });

  return request;
}

/**
 * Returns emergency requests, optionally filtered by document ID.
 * @param {string|null} docId - pass null for all requests
 * @param {string|null} status - filter by status
 */
export function getEmergencyRequests(docId = null, status = null) {
  let list = getAllRequests();
  if (docId) list = list.filter(r => String(r.docId) === String(docId));
  if (status) list = list.filter(r => r.status === status);
  return list;
}

/**
 * Returns pending emergency requests (for the owner's review queue).
 */
export function getPendingEmergencyRequests() {
  return getEmergencyRequests(null, 'pending');
}

/**
 * Approves an emergency release request.
 * @param {string} requestId
 * @param {string} reviewerNotes
 * @param {number} accessWindowHours - how long the release is valid (default 72h)
 */
export async function approveEmergencyRequest(requestId, reviewerNotes = '', accessWindowHours = 72) {
  const user = getSession();
  const list = getAllRequests();
  const req = list.find(r => r.id === requestId);
  if (!req) return null;

  req.status = 'approved';
  req.reviewedAt = new Date().toISOString();
  req.reviewedBy = user?.name || user?.email || 'Owner';
  req.reviewerNotes = reviewerNotes;
  req.releasedAt = new Date().toISOString();
  const expires = new Date();
  expires.setHours(expires.getHours() + accessWindowHours);
  req.expiresAt = expires.toISOString();
  req.timeline.push({
    event: 'request_approved',
    timestamp: new Date().toISOString(),
    actor: req.reviewedBy,
    detail: `Emergency release approved. Access valid for ${accessWindowHours} hours.`,
  });

  saveRequests(list);

  logAccess(req.docId, 'authorize', {
    docName: req.docName,
    action: 'emergency_release_approved',
    requesterName: req.requester.name,
    reviewerNotes,
  });

  return req;
}

/**
 * Denies an emergency release request.
 * @param {string} requestId
 * @param {string} reviewerNotes - reason for denial
 */
export function denyEmergencyRequest(requestId, reviewerNotes = '') {
  const user = getSession();
  const list = getAllRequests();
  const req = list.find(r => r.id === requestId);
  if (!req) return null;

  req.status = 'denied';
  req.reviewedAt = new Date().toISOString();
  req.reviewedBy = user?.name || user?.email || 'Owner';
  req.reviewerNotes = reviewerNotes;
  req.timeline.push({
    event: 'request_denied',
    timestamp: new Date().toISOString(),
    actor: req.reviewedBy,
    detail: reviewerNotes ? `Request denied: ${reviewerNotes}` : 'Request denied by owner.',
  });

  saveRequests(list);

  logAccess(req.docId, 'authorize', {
    docName: req.docName,
    action: 'emergency_release_denied',
    requesterName: req.requester.name,
    reviewerNotes,
  });

  return req;
}

/**
 * Revokes a previously approved emergency release (before it expires).
 */
export function revokeEmergencyRequest(requestId, reason = '') {
  const user = getSession();
  const list = getAllRequests();
  const req = list.find(r => r.id === requestId);
  if (!req) return null;

  req.status = 'expired';
  req.timeline.push({
    event: 'release_revoked',
    timestamp: new Date().toISOString(),
    actor: user?.name || user?.email || 'Owner',
    detail: reason ? `Release revoked: ${reason}` : 'Release revoked by owner.',
  });
  req.expiresAt = new Date().toISOString();

  saveRequests(list);
  return req;
}

/**
 * Checks if a request's access window has expired and updates its status.
 */
export function checkExpiredRequests() {
  const list = getAllRequests();
  const now = new Date();
  let changed = false;

  list.forEach(req => {
    if (req.status === 'approved' && req.expiresAt) {
      if (new Date(req.expiresAt) < now) {
        req.status = 'expired';
        req.timeline.push({
          event: 'access_window_expired',
          timestamp: now.toISOString(),
          actor: 'system',
          detail: 'Emergency access window has expired.',
        });
        changed = true;
      }
    }
  });

  if (changed) saveRequests(list);
  return list.filter(r => r.status === 'expired');
}

/**
 * Clears all emergency requests, or only for a specific document.
 */
export function clearEmergencyRequests(docId = null) {
  try {
    if (docId) {
      const list = getAllRequests().filter(r => String(r.docId) !== String(docId));
      saveRequests(list);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch { /* private mode */ }
}

/**
 * Returns summary counts for the owner's dashboard.
 */
export function getEmergencySummary() {
  const list = getAllRequests();
  return {
    total: list.length,
    pending: list.filter(r => r.status === 'pending').length,
    approved: list.filter(r => r.status === 'approved').length,
    denied: list.filter(r => r.status === 'denied').length,
    released: list.filter(r => r.status === 'released').length,
    expired: list.filter(r => r.status === 'expired').length,
  };
}